import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getCentralToday } from "@/utils/dateHelpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, FileText, Image, FileDown, Filter, Pencil, Trash2, X, Archive, LogOut
} from "lucide-react";
import { useAdmin } from "./AdminContext";
import { MonthDropdown } from "./MonthDropdown";
import { SignatureCell } from "./SignatureCell";

export function SignInTab() {
  const { API, records, startDate, endDate, setStartDate, setEndDate, isFiltered, setIsFiltered, handleClearFilter, fetchData } = useAdmin();

  // Sign-In sheet always uses check_in_date (semantic: guests sign at arrival).
  // Use a local apply-filter handler so the shared context's dateField preference
  // doesn't leak into this tab.
  const handleApplyFilter = () => {
    if (startDate || endDate) {
      setIsFiltered(true);
      fetchData(startDate, endDate, 0, 'check_in_date');
      toast.success('Filter applied');
    }
  };
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showManualEntryForm, setShowManualEntryForm] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({
    employee_id: "", first_name: "", last_name: "", room_number: "",
    check_in_date: getCentralToday(), check_in_time: "14:00",
    check_out_date: "", check_out_time: "11:00", is_checked_out: false
  });
  const [manualEntryLoading, setManualEntryLoading] = useState(false);
  const [employeeLookupLoading, setEmployeeLookupLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [forceCheckoutConfirm, setForceCheckoutConfirm] = useState(null);

  // Sign-in sheets are always grouped by check-in date (the moment guests sign).
  // Re-fetch using check_in_date when this tab mounts so we don't display
  // a stale check_out_date-filtered list left over from the Dashboard tab.
  useEffect(() => {
    fetchData(startDate || null, endDate || null, 0, 'check_in_date');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookupEmployee = async (empId) => {
    if (!empId || empId.length < 3) return;
    setEmployeeLookupLoading(true);
    try {
      const res = await axios.get(`${API}/admin/employee-lookup/${empId}`);
      if (res.data.found) {
        setManualEntryForm(prev => ({
          ...prev,
          first_name: res.data.first_name || prev.first_name,
          last_name: res.data.last_name || prev.last_name
        }));
        if (res.data.has_signature) {
          toast.success("Employee found — name & signature auto-filled");
        } else {
          toast.success("Employee found — name auto-filled");
        }
      }
    } catch (e) { /* silently ignore lookup errors */ }
    setEmployeeLookupLoading(false);
  };

  const handleManualEntry = async () => {
    if (!manualEntryForm.employee_id || !manualEntryForm.first_name || !manualEntryForm.last_name || !manualEntryForm.room_number) { toast.error("Please fill in all required fields"); return; }
    setManualEntryLoading(true);
    try {
      const response = await axios.post(`${API}/admin/manual-entry`, manualEntryForm);
      toast.success(response.data.message);
      if (response.data.billing) toast.info(`Billing: ${response.data.billing.nights} nights = $${response.data.billing.total}`);
      setShowManualEntryForm(false);
      setManualEntryForm({ employee_id: "", first_name: "", last_name: "", room_number: "", check_in_date: getCentralToday(), check_in_time: "14:00", check_out_date: "", check_out_time: "11:00", is_checked_out: false });
      fetchData(startDate || null, endDate || null, 0, 'check_in_date');
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create entry"); }
    setManualEntryLoading(false);
  };

  const handleExportSignIn = async () => {
    try {
      const response = await axios.get(`${API}/admin/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", "hodler_inn_sign_in_sheet.xlsx"); document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("Sign-In Sheet (Excel) exported!");
    } catch (error) { toast.error("Failed to export"); }
  };

  const handleExportSignInPng = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-png`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", "hodler_inn_sign_in_sheet.png"); document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("Sign-In Sheet (PNG) exported!");
    } catch (error) { toast.error("Failed to export"); }
  };

  const handleExportSignInPdf = async () => {
    try {
      let url = `${API}/admin/export-pdf`;
      const params = new URLSearchParams();
      if (startDate) params.push ? params.append('start_date', startDate) : null;
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = blobUrl; link.setAttribute("download", "hodler_inn_sign_in_sheet.pdf"); document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
      toast.success("Sign-In Sheet (PDF) exported!");
    } catch (error) { toast.error("Failed to export PDF"); }
  };

  // Bundle Billing + Sign-In PDFs into a single ZIP for the railroad. Uses the
  // currently applied date filter. Server applies the same range to both
  // (billing buckets by check-out date, sign-in by check-in date).
  const handleExportRailroadZip = async () => {
    try {
      if (!startDate || !endDate) {
        toast.error("Please apply a Start and End date filter before downloading the railroad ZIP.");
        return;
      }
      toast.info("Generating ZIP… this can take 10–30 seconds for a full month. Please don't close this tab.");
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const response = await axios.get(`${API}/admin/export-railroad-zip?${params.toString()}`, {
        responseType: "blob",
        timeout: 180000, // 3 min — plenty of headroom for a full-month bundle through the ingress
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Hodler_Inn_Railroad_${startDate}_to_${endDate}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Railroad ZIP downloaded!");
    } catch (error) {
      const msg = error?.code === "ECONNABORTED"
        ? "Download timed out. Try a smaller date range or retry."
        : "Failed to generate Railroad ZIP";
      toast.error(msg);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditForm({
      employee_number: record.employee_number || "",
      employee_name: record.employee_name || "",
      room_number: record.room_number || "",
      check_in_date: record.check_in_date || "",
      check_in_time: record.check_in_time || "",
      check_out_date: record.check_out_date || "",
      check_out_time: record.check_out_time || "",
    });
  };

  const handleSaveEdit = async () => {
    try {
      if (editForm.employee_number && !/^\d*$/.test(editForm.employee_number)) { toast.error("Employee number must contain only numbers"); return; }
      // Only send fields that have values — the backend BookingUpdate model ignores
      // None values, but we additionally strip empty strings here so a user clearing
      // a field doesn't blank out the stored value unintentionally.
      const payload = Object.fromEntries(
        Object.entries(editForm).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      await axios.put(`${API}/admin/bookings/${editingRecord.id}`, payload);
      toast.success("Record updated");
      setEditingRecord(null);
      fetchData(startDate || null, endDate || null, 0, 'check_in_date');
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success("Record deleted successfully");
      setDeleteConfirm(null);
      fetchData(startDate || null, endDate || null, 0, 'check_in_date');
    } catch (error) { toast.error("Failed to delete record"); }
  };

  // Force checkout: bypass the 9-hour minimum-stay rule so admin can free a
  // stuck room (e.g. guest checked in by mistake, or already left without
  // checking out). Uses the existing /admin/force-checkout/{room} endpoint
  // which flips is_checked_out, stamps the current Central time, and marks
  // the room dirty for housekeeping.
  const handleForceCheckout = async (record) => {
    try {
      await axios.post(`${API}/admin/force-checkout/${record.room_number}`);
      toast.success(`Room ${record.room_number} forcibly checked out`);
      setForceCheckoutConfirm(null);
      fetchData(startDate || null, endDate || null, 0, 'check_in_date');
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to force checkout"); }
  };

  const filteredRecords = showActiveOnly ? records.filter(r => !r.is_checked_out) : records;

  return (
    <>
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Sign-In Sheet</h1>
            <p className="text-vault-text-secondary font-manrope mt-1">View all guest sign-in records</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowManualEntryForm(true)} className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2" data-testid="manual-entry-btn"><Plus className="w-4 h-4" />Add Entry</Button>
            <Button onClick={handleExportSignIn} className="vault-btn-primary flex items-center gap-2" data-testid="export-signin-excel-btn"><FileText className="w-4 h-4" />Excel</Button>
            <Button onClick={handleExportSignInPng} className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2" data-testid="export-signin-png-btn"><Image className="w-4 h-4" />PNG</Button>
            <Button onClick={handleExportSignInPdf} className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2" data-testid="export-signin-pdf-btn"><FileDown className="w-4 h-4" />PDF</Button>
            <Button
              onClick={handleExportRailroadZip}
              className="bg-vault-gold hover:bg-amber-500 text-black font-semibold flex items-center gap-2"
              data-testid="export-railroad-zip-btn"
              title="Bundle Billing + Sign-In PDFs for the railroad (uses the date filter above)"
            >
              <Archive className="w-4 h-4" />Railroad ZIP
            </Button>
          </div>

          <Dialog open={showManualEntryForm} onOpenChange={setShowManualEntryForm}>
            <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
              <DialogHeader><DialogTitle className="text-vault-gold flex items-center gap-2"><Plus className="w-5 h-5" />Add Backdated Entry</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-vault-text-secondary text-xs block mb-1">Employee ID *</label><Input value={manualEntryForm.employee_id} onChange={(e) => setManualEntryForm({...manualEntryForm, employee_id: e.target.value})} onBlur={(e) => lookupEmployee(e.target.value)} placeholder="Enter employee number" className="bg-black/50 border-vault-border text-vault-text" />{employeeLookupLoading && <span className="text-vault-gold text-xs">Looking up...</span>}</div>
                  <div><label className="text-vault-text-secondary text-xs block mb-1">Room # *</label><Input value={manualEntryForm.room_number} onChange={(e) => setManualEntryForm({...manualEntryForm, room_number: e.target.value})} placeholder="101" className="bg-black/50 border-vault-border text-vault-text" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-vault-text-secondary text-xs block mb-1">First Name *</label><Input value={manualEntryForm.first_name} onChange={(e) => setManualEntryForm({...manualEntryForm, first_name: e.target.value})} placeholder="John" className="bg-black/50 border-vault-border text-vault-text" /></div>
                  <div><label className="text-vault-text-secondary text-xs block mb-1">Last Name *</label><Input value={manualEntryForm.last_name} onChange={(e) => setManualEntryForm({...manualEntryForm, last_name: e.target.value})} placeholder="Smith" className="bg-black/50 border-vault-border text-vault-text" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-vault-text-secondary text-xs block mb-1">Check-In Date *</label><Input type="date" value={manualEntryForm.check_in_date} onChange={(e) => setManualEntryForm({...manualEntryForm, check_in_date: e.target.value})} className="bg-black/50 border-vault-border text-vault-text" /></div>
                  <div><label className="text-vault-text-secondary text-xs block mb-1">Check-In Time *</label><Input type="time" value={manualEntryForm.check_in_time} onChange={(e) => setManualEntryForm({...manualEntryForm, check_in_time: e.target.value})} className="bg-black/50 border-vault-border text-vault-text" /></div>
                </div>
                <div className="flex items-center gap-2 bg-black/30 p-3 rounded-lg">
                  <input type="checkbox" checked={manualEntryForm.is_checked_out} onChange={(e) => setManualEntryForm({...manualEntryForm, is_checked_out: e.target.checked})} className="w-4 h-4 accent-vault-gold" />
                  <label className="text-vault-text text-sm">Already Checked Out</label>
                </div>
                {manualEntryForm.is_checked_out && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-vault-text-secondary text-xs block mb-1">Check-Out Date</label><Input type="date" value={manualEntryForm.check_out_date} onChange={(e) => setManualEntryForm({...manualEntryForm, check_out_date: e.target.value})} className="bg-black/50 border-vault-border text-vault-text" /></div>
                    <div><label className="text-vault-text-secondary text-xs block mb-1">Check-Out Time</label><Input type="time" value={manualEntryForm.check_out_time} onChange={(e) => setManualEntryForm({...manualEntryForm, check_out_time: e.target.value})} className="bg-black/50 border-vault-border text-vault-text" /></div>
                  </div>
                )}
                <p className="text-vault-text-secondary text-xs">* Required fields. Entry will be added to billing records.</p>
              </div>
              <DialogFooter className="gap-2">
                <DialogClose asChild><Button variant="outline" className="border-vault-border text-vault-text">Cancel</Button></DialogClose>
                <Button onClick={handleManualEntry} disabled={manualEntryLoading} className="bg-vault-gold hover:bg-vault-gold/80 text-black">{manualEntryLoading ? "Adding..." : "Add Entry"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 bg-vault-surface-highlight/50 p-3 rounded-lg border border-vault-border">
          <Filter className="w-4 h-4 text-vault-gold" />
          <span className="text-vault-text-secondary text-sm font-medium">Filter:</span>
          <div className="flex items-center gap-2 bg-black/30 px-3 py-1.5 rounded-lg border border-vault-border">
            <label className="text-vault-text-secondary text-sm cursor-pointer flex items-center gap-2">
              <input type="checkbox" checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} className="w-4 h-4 accent-vault-gold cursor-pointer" data-testid="show-active-only-toggle" />
              <span className={showActiveOnly ? "text-vault-gold font-medium" : ""}>In-House Only</span>
            </label>
          </div>
          <div className="h-6 w-px bg-vault-border mx-1" />
          <MonthDropdown dateFieldOverride="check_in_date" />
          <span className="text-vault-text-secondary text-sm">or custom:</span>
          <DatePicker date={startDate} onDateChange={setStartDate} placeholder="Start Date" className="h-9" />
          <span className="text-vault-text-secondary">to</span>
          <DatePicker date={endDate} onDateChange={setEndDate} placeholder="End Date" className="h-9" />
          <Button onClick={handleApplyFilter} className="vault-btn-primary h-9 px-4" data-testid="apply-filter-btn">Apply</Button>
          {isFiltered && <Button onClick={handleClearFilter} variant="ghost" className="h-9 px-3 text-vault-text-secondary hover:text-vault-gold" data-testid="clear-filter-btn"><X className="w-4 h-4 mr-1" />Clear</Button>}
        </div>
        <div className="mt-2 text-sm text-vault-text-secondary" data-testid="signin-records-count-label">
          Showing <span className="text-vault-gold font-medium">{filteredRecords.length}</span> of <span className="text-vault-text">{records.length}</span> records
        </div>
        {showActiveOnly && <div className="mt-1 text-sm text-vault-text-secondary">Showing <span className="text-vault-gold font-medium">{records.filter(r => !r.is_checked_out).length}</span> in-house guests</div>}
      </div>

      <Card className="bg-vault-surface border-vault-border mb-6" data-testid="signin-sheet-card">
        <CardHeader className="border-b border-vault-border text-center py-4">
          <CardTitle className="font-outfit text-2xl text-vault-gold">Hodler Inn</CardTitle>
          <p className="text-vault-text-secondary text-sm">820 Hwy 59 N Heavener, OK, 74937</p>
          <p className="text-vault-text-secondary text-sm">Phone: 918-653-7801</p>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <div className="min-w-[1200px]">
            <Table>
              <TableHeader>
                <TableRow className="table-header border-vault-border hover:bg-transparent">
                  <TableHead className="text-vault-gold w-12">#</TableHead><TableHead className="text-vault-gold">Stay Type</TableHead><TableHead className="text-vault-gold">Name</TableHead><TableHead className="text-vault-gold">Employee ID</TableHead><TableHead className="text-vault-gold">Signature In</TableHead><TableHead className="text-vault-gold">Signature Out</TableHead><TableHead className="text-vault-gold">Date In</TableHead><TableHead className="text-vault-gold">Time In</TableHead><TableHead className="text-vault-gold">Date Out</TableHead><TableHead className="text-vault-gold">Time Out</TableHead><TableHead className="text-vault-gold">Room #</TableHead><TableHead className="text-vault-gold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center text-vault-text-secondary py-8">{showActiveOnly ? "No in-house guests found" : "No records found"}</TableCell></TableRow>
                ) : filteredRecords.map((record, index) => (
                  <TableRow key={record.id} className="table-row border-vault-border" data-testid={`signin-row-${record.id}`}>
                    <TableCell className="font-mono text-vault-text">{index + 1}</TableCell>
                    <TableCell className="text-vault-text">Single Stay</TableCell>
                    <TableCell className="text-vault-text font-medium">{record.employee_name}</TableCell>
                    <TableCell className="font-mono text-vault-text">{record.employee_number}</TableCell>
                    <TableCell><SignatureCell record={record} API={API} variant="icon" /></TableCell>
                    <TableCell><SignatureCell record={record} API={API} variant="icon" requireCheckedOut /></TableCell>
                    <TableCell className="text-vault-text">{record.check_in_date}</TableCell>
                    <TableCell className="font-mono text-vault-text">{record.check_in_time}</TableCell>
                    <TableCell className="text-vault-text">{record.check_out_date || "—"}</TableCell>
                    <TableCell className="font-mono text-vault-text">{record.check_out_time || "—"}</TableCell>
                    <TableCell className="font-mono text-vault-gold font-bold">{record.room_number}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="text-vault-text-secondary hover:text-vault-gold h-8 w-8 p-0" onClick={() => handleEdit(record)} data-testid={`edit-${record.id}`}><Pencil className="w-4 h-4" /></Button>
                        {!record.is_checked_out && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-vault-text-secondary hover:text-amber-500 h-8 w-8 p-0"
                            onClick={() => setForceCheckoutConfirm(record)}
                            data-testid={`force-checkout-${record.id}`}
                            title="Force checkout (bypasses minimum stay rule)"
                          >
                            <LogOut className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-vault-text-secondary hover:text-red-500 h-8 w-8 p-0" onClick={() => setDeleteConfirm(record)} data-testid={`delete-${record.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <p className="text-vault-text-secondary text-xs text-center md:hidden">Swipe table horizontally to see all columns</p>

      {/* Edit Record Dialog */}
      <Dialog open={!!editingRecord} onOpenChange={(open) => !open && setEditingRecord(null)}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit text-vault-text">Edit Guest Record</DialogTitle></DialogHeader>
          {editingRecord && (
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              <div><label className="text-sm text-vault-text-secondary mb-2 block">Employee Number</label><Input value={editForm.employee_number} onChange={(e) => setEditForm({...editForm, employee_number: e.target.value.replace(/[^0-9]/g, '')})} className="vault-input font-mono" data-testid="edit-signin-employee-number" /></div>
              <div><label className="text-sm text-vault-text-secondary mb-2 block">Employee Name</label><Input value={editForm.employee_name} onChange={(e) => setEditForm({...editForm, employee_name: e.target.value})} className="vault-input" data-testid="edit-signin-employee-name" /></div>
              <div><label className="text-sm text-vault-text-secondary mb-2 block">Room Number</label><Input value={editForm.room_number} onChange={(e) => setEditForm({...editForm, room_number: e.target.value})} className="vault-input font-mono" data-testid="edit-signin-room-number" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-In Date</label>
                  <Input type="date" value={editForm.check_in_date} onChange={(e) => setEditForm({...editForm, check_in_date: e.target.value})} className="vault-input font-mono" data-testid="edit-signin-check-in-date" />
                </div>
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-In Time</label>
                  <Input type="time" value={editForm.check_in_time} onChange={(e) => setEditForm({...editForm, check_in_time: e.target.value})} className="vault-input font-mono" data-testid="edit-signin-check-in-time" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-Out Date</label>
                  <Input type="date" value={editForm.check_out_date} onChange={(e) => setEditForm({...editForm, check_out_date: e.target.value})} className="vault-input font-mono" data-testid="edit-signin-check-out-date" />
                </div>
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-Out Time</label>
                  <Input type="time" value={editForm.check_out_time} onChange={(e) => setEditForm({...editForm, check_out_time: e.target.value})} className="vault-input font-mono" data-testid="edit-signin-check-out-time" />
                </div>
              </div>
              <p className="text-xs text-vault-text-secondary italic">Tip: if you set both Check-Out Date <em>and</em> Check-Out Time here, the guest will be auto-marked checked out and totals will be recomputed. Leave a time field blank to keep its current value.</p>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" className="text-vault-text-secondary">Cancel</Button></DialogClose>
            <Button onClick={handleSaveEdit} className="vault-btn-primary" data-testid="save-signin-edit-btn">Update Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-vault-surface border-vault-border">
          <DialogHeader><DialogTitle className="font-outfit text-vault-text">Delete Record</DialogTitle></DialogHeader>
          <p className="text-vault-text-secondary">Are you sure you want to delete this record for <span className="text-vault-gold">{deleteConfirm?.employee_name}</span>?</p>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" className="text-vault-text-secondary">Cancel</Button></DialogClose>
            <Button onClick={() => handleDelete(deleteConfirm?.id)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-record-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Checkout Confirmation Dialog */}
      <Dialog open={!!forceCheckoutConfirm} onOpenChange={() => setForceCheckoutConfirm(null)}>
        <DialogContent className="bg-vault-surface border-vault-border">
          <DialogHeader><DialogTitle className="font-outfit text-vault-text">Force Checkout</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-vault-text-secondary">
              Force-check-out <span className="text-vault-gold">{forceCheckoutConfirm?.employee_name}</span> from
              {" "}Room <span className="text-vault-gold font-mono">{forceCheckoutConfirm?.room_number}</span>?
            </p>
            <p className="text-xs text-amber-400">
              ⚠ This bypasses the 9-hour minimum-stay rule. Use when a guest needs to leave early or a check-in was made by mistake. The room will be marked dirty for housekeeping and freed for the next crew.
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" className="text-vault-text-secondary">Cancel</Button></DialogClose>
            <Button
              onClick={() => handleForceCheckout(forceCheckoutConfirm)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-force-checkout-btn"
            >
              Force Checkout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
